import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { StatsGrid, StatItem, HorizontalCategoryTabs } from '../../../shared/ui';
import { sessionsV2Api, SiteWorkTypesSummary, SiteUsersSummary } from '../../../api/sessionsV2Api';
import './WorkTypesSummaryCard.css';

interface WorkTypesSummaryCardProps {
  siteId?: number;
}

type ViewMode = 'workTypes' | 'users';

type SummaryTotals = Pick<
  SiteWorkTypesSummary,
  | 'totalHours'
  | 'workAccruedAmount'
  | 'bonusAccruedAmount'
  | 'totalAmount'
  | 'paidSalaryAmount'
  | 'paidBonusAmount'
  | 'paidAmount'
  | 'unallocatedPaymentAmount'
  | 'unpaidBonusAmount'
  | 'remainingAmount'
  | 'hasFinancialAccess'
>;

const FinancialSummarySection: React.FC<{ data: SummaryTotals }> = ({ data }) => {
  if (!data.hasFinancialAccess) return null;

  const unalloc = data.unallocatedPaymentAmount ?? 0;

  const rows: { label: string; value: number; icon: string; accent?: boolean }[] = [
    { label: 'По ставке (часы × ставка)', value: data.workAccruedAmount, icon: 'ri-money-ruble-circle-line' },
    { label: 'Премии начислено', value: data.bonusAccruedAmount, icon: 'ri-gift-line' },
    { label: 'Заработано всего', value: data.totalAmount, icon: 'ri-wallet-3-line', accent: true },
    { label: 'Выплачено (зарплата)', value: data.paidSalaryAmount, icon: 'ri-bank-card-line' },
    { label: 'Выплачено премий', value: data.paidBonusAmount, icon: 'ri-award-line' },
    {
      label: 'На платежах без распределения по активностям',
      value: unalloc,
      icon: 'ri-funds-line',
    },
    { label: 'Всего выплачено', value: data.paidAmount, icon: 'ri-checkbox-circle-line', accent: true },
    { label: 'Премии к выплате', value: data.unpaidBonusAmount, icon: 'ri-time-line' },
    { label: 'Остаток', value: data.remainingAmount, icon: 'ri-file-list-line' },
  ];

  return (
    <div className="site-summary-financial" role="region" aria-label="Финансовая сводка">
      {rows.map((row) => (
        <div
          key={row.label}
          className={`site-summary-financial__row${row.accent ? ' site-summary-financial__row--accent' : ''}`}
        >
          <span className="site-summary-financial__label">
            <i className={row.icon} aria-hidden />
            {row.label}
          </span>
          <span className="site-summary-financial__value">
            {new Intl.NumberFormat('ru-RU', {
              style: 'currency',
              currency: 'RUB',
              minimumFractionDigits: 2,
            }).format(row.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const WorkTypesSummaryCard: React.FC<WorkTypesSummaryCardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('workTypes');
  const [workTypesData, setWorkTypesData] = useState<SiteWorkTypesSummary | null>(null);
  const [usersData, setUsersData] = useState<SiteUsersSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedWorkTypes, setExpandedWorkTypes] = useState<Set<number>>(new Set());
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());

  const currentData: SiteWorkTypesSummary | SiteUsersSummary | null =
    viewMode === 'workTypes' ? workTypesData : usersData;

  const loadWorkTypesData = useCallback(async () => {
    if (!siteId) {
      setWorkTypesData(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await sessionsV2Api.getWorkTypesSummary(siteId);
      setWorkTypesData(result);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      console.error('Error loading work types summary:', err);
      setError(e.response?.data?.message || e.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const loadUsersData = useCallback(async () => {
    if (!siteId) {
      setUsersData(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await sessionsV2Api.getUsersSummary(siteId);
      setUsersData(result);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      console.error('Error loading users summary:', err);
      setError(e.response?.data?.message || e.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (!isCollapsed && siteId) {
      if (viewMode === 'workTypes') {
        if (!workTypesData) {
          loadWorkTypesData();
        }
      } else if (!usersData) {
        loadUsersData();
      }
    }
  }, [isCollapsed, siteId, viewMode, workTypesData, usersData, loadWorkTypesData, loadUsersData]);

  const handleViewModeChange = (mode: string | null) => {
    if (mode === 'По сотрудникам') {
      setViewMode('users');
    } else {
      setViewMode('workTypes');
    }
    setError(null);
  };

  const toggleWorkType = (typeOfWorkId: number) => {
    setExpandedWorkTypes((prev) => {
      const next = new Set(prev);
      if (next.has(typeOfWorkId)) next.delete(typeOfWorkId);
      else next.add(typeOfWorkId);
      return next;
    });
  };

  const toggleUser = (userId: number) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const formatAmount = (amount: number): string =>
    new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2,
    }).format(amount);

  const formatHours = (hours: number): string => `${hours.toFixed(2)} ч`;

  const statsItems = useMemo<StatItem[]>(() => {
    const data = currentData;
    if (!data) return [];

    const items: StatItem[] = [
      {
        id: 'total-hours',
        value: formatHours(data.totalHours),
        label: 'Всего часов',
        icon: 'ri-time-line',
        color: 'muted',
      },
    ];

    return items;
  }, [currentData]);

  const handleRefresh = () => {
    if (viewMode === 'workTypes') {
      loadWorkTypesData();
    } else {
      loadUsersData();
    }
  };

  return (
    <DashboardCard
      id="site-summary-card"
      title="Сводка по объекту"
      icon="ri-bar-chart-box-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      expandMode="fullscreen"
      headerActions={
        siteId ? (
          <button
            type="button"
            className="card-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
            }}
            disabled={loading}
            title="Обновить данные"
          >
            <i className={loading ? 'ri-loader-4-line ri-spin' : 'ri-refresh-line'} />
          </button>
        ) : null
      }
    >
      {!siteId ? (
        <div className="work-types-summary-placeholder">
          <i className="ri-bar-chart-box-line" />
          <h3>Выберите проект</h3>
          <small>Для просмотра сводки по объекту необходимо выбрать конкретный проект</small>
        </div>
      ) : (
        <div className="work-types-summary-content">
          {loading && !currentData && (
            <div className="work-types-summary-loading">
              <i className="ri-loader-4-line ri-spin" />
              <span>Загрузка данных...</span>
            </div>
          )}

          {error && (
            <div className="work-types-summary-error">
              <i className="ri-error-warning-line" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && !currentData && (
            <div className="work-types-summary-empty">
              <span>Нет данных</span>
            </div>
          )}

          {currentData && (
            <>
              <StatsGrid items={statsItems} variant="compact" />
              {currentData.hasFinancialAccess && <FinancialSummarySection data={currentData} />}

              <HorizontalCategoryTabs
                categories={['По сотрудникам']}
                value={viewMode === 'users' ? 'По сотрудникам' : null}
                onChange={handleViewModeChange}
                showAllOption={true}
                allOptionLabel="По видам работ"
                size="medium"
                className="summary-view-mode-tabs"
              />

              {viewMode === 'workTypes' && workTypesData && (
                <>
                  {workTypesData.workTypes.length === 0 ? (
                    <div className="work-types-summary-empty">
                      <i className="ri-inbox-line" />
                      <span>Нет данных по видам работ</span>
                    </div>
                  ) : (
                    <div className="work-types-list">
                      {workTypesData.workTypes.map((workType, index) => {
                        const isExpanded = expandedWorkTypes.has(workType.typeOfWorkId);
                        return (
                          <div key={workType.typeOfWorkId}>
                            <div
                              className="work-type-header"
                              onClick={() => toggleWorkType(workType.typeOfWorkId)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleWorkType(workType.typeOfWorkId);
                                }
                              }}
                            >
                              <div className="work-type-info">
                                <div className="work-type-name-row">
                                  <span className="work-type-name">{workType.typeOfWorkName}</span>
                                  <span className="work-type-stat">
                                    <i className="ri-time-line" />
                                    {formatHours(workType.totalHours)}
                                  </span>
                                  {workType.hasFinancialAccess && (
                                    <>
                                      <span className="work-type-stat-divider">|</span>
                                      <span className="work-type-stat" title="Заработано всего">
                                        <i className="ri-wallet-3-line" />
                                        {formatAmount(workType.totalAmount)}
                                      </span>
                                      <span className="work-type-stat-divider">|</span>
                                      <span className="work-type-stat" title="Всего выплачено">
                                        <i className="ri-checkbox-circle-line" />
                                        {formatAmount(workType.paidAmount)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line`} />
                            </div>

                            {isExpanded && workType.hasFinancialAccess && (
                              <div className="work-type-financial-detail">
                                <div className="work-type-financial-detail__row">
                                  <span>По ставке</span>
                                  <span>{formatAmount(workType.workAccruedAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Премии начислено</span>
                                  <span>{formatAmount(workType.bonusAccruedAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row work-type-financial-detail__row--accent">
                                  <span>Заработано всего</span>
                                  <span>{formatAmount(workType.totalAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Выплачено (зарплата)</span>
                                  <span>{formatAmount(workType.paidSalaryAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Выплачено премий</span>
                                  <span>{formatAmount(workType.paidBonusAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row work-type-financial-detail__row--accent">
                                  <span>Всего выплачено</span>
                                  <span>{formatAmount(workType.paidAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Премии к выплате</span>
                                  <span>{formatAmount(workType.unpaidBonusAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Остаток</span>
                                  <span>{formatAmount(workType.remainingAmount)}</span>
                                </div>
                              </div>
                            )}

                            {isExpanded && (
                              <div className="work-type-additional-stats">
                                <span className="work-type-stat">
                                  <i className="ri-user-line" />
                                  {workType.users.length}
                                </span>
                              </div>
                            )}

                            {isExpanded && (
                              <div className="work-type-users">
                                {workType.users.map((user, userIndex) => (
                                  <React.Fragment key={user.userId}>
                                    {userIndex > 0 && <div className="user-divider" />}
                                    <div className="work-type-user">
                                      <div className="work-type-user-name">
                                        {user.userName}
                                        {user.userClosed && <span className="user-closed-badge"> (закрыт)</span>}
                                      </div>
                                      <div className="work-type-user-stats">
                                        <span className="user-stat">
                                          <i className="ri-time-line" />
                                          {formatHours(user.totalHours)}
                                        </span>
                                        {workType.hasFinancialAccess && (
                                          <>
                                            <span className="user-stat-divider">|</span>
                                            <span className="user-stat" title="Заработано всего">
                                              <i className="ri-wallet-3-line" />
                                              {formatAmount(user.totalAmount)}
                                            </span>
                                            <span className="user-stat-divider">|</span>
                                            <span className="user-stat" title="Всего выплачено">
                                              <i className="ri-checkbox-circle-line" />
                                              {formatAmount(user.paidAmount)}
                                            </span>
                                            <span className="user-stat-divider">|</span>
                                            <span className="user-stat" title="Остаток">
                                              <i className="ri-file-list-line" />
                                              {formatAmount(user.remainingAmount)}
                                            </span>
                                          </>
                                        )}
                                        <span className="user-stat-divider">|</span>
                                        <span className="user-stat">
                                          <i className="ri-calendar-line" />
                                          {user.sessionsCount}
                                        </span>
                                        <span className="user-stat-divider">|</span>
                                        <span className="user-stat">
                                          <i className="ri-file-list-3-line" />
                                          {user.activitiesCount}
                                        </span>
                                      </div>
                                    </div>
                                  </React.Fragment>
                                ))}
                              </div>
                            )}
                            {index < workTypesData.workTypes.length - 1 && <div className="work-type-divider" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {viewMode === 'users' && usersData && (
                <>
                  {usersData.users.length === 0 ? (
                    <div className="work-types-summary-empty">
                      <i className="ri-inbox-line" />
                      <span>Нет данных по сотрудникам</span>
                    </div>
                  ) : (
                    <div className="users-list">
                      {usersData.users.map((user, index) => {
                        const isExpanded = expandedUsers.has(user.userId);
                        return (
                          <div key={user.userId}>
                            <div
                              className="user-header"
                              onClick={() => toggleUser(user.userId)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleUser(user.userId);
                                }
                              }}
                            >
                              <div className="user-info">
                                <div className="user-name-row">
                                  <span className="user-name">
                                    {user.userName}
                                    {user.userClosed && <span className="user-closed-badge"> (закрыт)</span>}
                                  </span>
                                  <span className="user-stat">
                                    <i className="ri-time-line" />
                                    {formatHours(user.totalHours)}
                                  </span>
                                  {user.hasFinancialAccess && (
                                    <>
                                      <span className="user-stat-divider">|</span>
                                      <span className="user-stat" title="Заработано всего">
                                        <i className="ri-wallet-3-line" />
                                        {formatAmount(user.totalAmount)}
                                      </span>
                                      <span className="user-stat-divider">|</span>
                                      <span className="user-stat" title="Всего выплачено">
                                        <i className="ri-checkbox-circle-line" />
                                        {formatAmount(user.paidAmount)}
                                      </span>
                                      <span className="user-stat-divider">|</span>
                                      <span
                                        className="user-stat"
                                        title="На платежах без распределения по активностям"
                                      >
                                        <i className="ri-funds-line" />
                                        {formatAmount(user.unallocatedPaymentAmount ?? 0)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line`} />
                            </div>

                            {isExpanded && user.hasFinancialAccess && (
                              <div className="work-type-financial-detail user-financial-detail">
                                <div className="work-type-financial-detail__row">
                                  <span>По ставке</span>
                                  <span>{formatAmount(user.workAccruedAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Премии начислено</span>
                                  <span>{formatAmount(user.bonusAccruedAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row work-type-financial-detail__row--accent">
                                  <span>Заработано всего</span>
                                  <span>{formatAmount(user.totalAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Выплачено (зарплата)</span>
                                  <span>{formatAmount(user.paidSalaryAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Выплачено премий</span>
                                  <span>{formatAmount(user.paidBonusAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>На платежах без распределения по активностям</span>
                                  <span>{formatAmount(user.unallocatedPaymentAmount ?? 0)}</span>
                                </div>
                                <div className="work-type-financial-detail__row work-type-financial-detail__row--accent">
                                  <span>Всего выплачено</span>
                                  <span>{formatAmount(user.paidAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Премии к выплате</span>
                                  <span>{formatAmount(user.unpaidBonusAmount)}</span>
                                </div>
                                <div className="work-type-financial-detail__row">
                                  <span>Остаток</span>
                                  <span>{formatAmount(user.remainingAmount)}</span>
                                </div>
                              </div>
                            )}

                            {isExpanded && (
                              <div className="user-additional-stats">
                                <span className="user-stat">
                                  <i className="ri-calendar-line" />
                                  {user.sessionsCount}
                                </span>
                                <span className="user-stat-divider">|</span>
                                <span className="user-stat">
                                  <i className="ri-file-list-3-line" />
                                  {user.activitiesCount}
                                </span>
                                <span className="user-stat-divider">|</span>
                                <span className="user-stat">
                                  <i className="ri-hammer-line" />
                                  {user.workTypes.length}
                                </span>
                              </div>
                            )}

                            {isExpanded && (
                              <div className="user-work-types">
                                {user.workTypes.map((workType, workTypeIndex) => (
                                  <React.Fragment key={workType.typeOfWorkId}>
                                    {workTypeIndex > 0 && <div className="work-type-divider" />}
                                    <div className="user-work-type">
                                      <div className="user-work-type-name">{workType.typeOfWorkName}</div>
                                      <div className="user-work-type-stats">
                                        <span className="work-type-stat">
                                          <i className="ri-time-line" />
                                          {formatHours(workType.totalHours)}
                                        </span>
                                        {user.hasFinancialAccess && (
                                          <>
                                            <span className="work-type-stat-divider">|</span>
                                            <span className="work-type-stat" title="Заработано всего">
                                              <i className="ri-wallet-3-line" />
                                              {formatAmount(workType.totalAmount)}
                                            </span>
                                            <span className="work-type-stat-divider">|</span>
                                            <span className="work-type-stat" title="Всего выплачено">
                                              <i className="ri-checkbox-circle-line" />
                                              {formatAmount(workType.paidAmount)}
                                            </span>
                                            <span className="work-type-stat-divider">|</span>
                                            <span className="work-type-stat" title="Остаток">
                                              <i className="ri-file-list-line" />
                                              {formatAmount(workType.remainingAmount)}
                                            </span>
                                          </>
                                        )}
                                        <span className="work-type-stat-divider">|</span>
                                        <span className="work-type-stat">
                                          <i className="ri-calendar-line" />
                                          {workType.sessionsCount}
                                        </span>
                                        <span className="work-type-stat-divider">|</span>
                                        <span className="work-type-stat">
                                          <i className="ri-file-list-3-line" />
                                          {workType.activitiesCount}
                                        </span>
                                      </div>
                                    </div>
                                  </React.Fragment>
                                ))}
                              </div>
                            )}
                            {index < usersData.users.length - 1 && <div className="user-divider" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </DashboardCard>
  );
};

export default WorkTypesSummaryCard;
